import { Fragment, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';

import { getContentTypeOptionsForUri } from 'components/ContentReporting/form-utils';
import SingleUriField from 'components/ContentReporting/Form/Fields/SingleUriField/SingleUriField';
import SelectField from 'components/Fields/SelectField/SelectField';
import {
  convertPlaylistV1ToV2,
  getValidSpotifyUri,
  isSpotifyUri,
  isValidSpotifyUri,
} from 'components/ContentReporting/validation-utils';
import { USE_LABEL_FROM_TRANSLATION_PREFIX } from 'components/ContentReporting/shared-constants';
import { FieldChangeData, MarketInfoProps } from 'etc/types';
import {
  Field,
  FieldProperties,
  PODCAST_ENTITY_TYPES,
} from 'components/ContentReporting/ContentPolicyReporting/content-policy-reporting-config';
import { ErrorMessage, OnFieldChangeFunction } from 'containers/ContentReporting/ContentPolicyReporting/utils/types';
import { withFullTranslationKey } from 'containers/ContentReporting/ContentPolicyReporting/utils/functions';
import { CONTENT_TYPES } from 'components/ContentReporting/content-types';
import { ContentType } from 'proto/generated/tintin_content_policy';
import { isPodcastReportingEnabledForMarket } from 'components/ContentReporting/market-helper';
import { withMarketInfo } from 'context/MarketInfo/MarketInfoProvider';
import { TintinClientProps, withTintinClient } from 'components/ContentReporting/withTintinClient';
import {
  associatedContentTypeFromJSON,
  EntityType,
  entityTypeFromJSON,
  ResolveAssetTypeRequest,
  ResolveAssetTypeResponse,
  ResolveEntityTypeResponse,
} from 'proto/generated/inspector_gadget';

import { ContextualInfoProps, withContextualInfo } from '../withContextualInfo';
import { ResolveEntityTypeRequest } from '../TintinClient';

const ASSET_EXISTENCE_CHECK_ENTITIES = [
  entityTypeFromJSON(EntityType.ENTITY_TYPE_PLAYLIST),
  entityTypeFromJSON(EntityType.ENTITY_TYPE_USER),
];

export const RESOLVE_ENTITY_TYPE_DEBOUNCE_TIME_IN_MS = 500;

const getUriErrorMessage = (
  uriValue: string,
  acceptedContentTypes: Array<string>,
  associatedContentError: string,
): string => {
  if (!isSpotifyUri(uriValue)) {
    return withFullTranslationKey('content_uri_invalid');
  }

  if (!isValidSpotifyUri(uriValue, acceptedContentTypes)) {
    return withFullTranslationKey('content_uri_not_accepted');
  }

  if (associatedContentError !== '') {
    return withFullTranslationKey(associatedContentError);
  }

  return '';
};

const handleAssetTypeChange =
  (parentHandleChange: OnFieldChangeFunction, fetchAssetType: (assetType: string) => void) =>
  (element: FieldChangeData): void => {
    parentHandleChange(element);
    // Only fetch associated content for valid URI
    if (element.valid && element.value !== '') {
      // Set loaded state to false here as fetchAssociatedContent is debounced
      fetchAssetType(element.value);
    }
  };

const handleUriInputChange =
  (
    previousValue: string,
    parentHandleChange: OnFieldChangeFunction,
    fetchEntityType: (uri: string) => void,
    setEntityType: (_: ResolveEntityType) => void,
  ) =>
  (element: FieldChangeData): void => {
    if (element.value !== previousValue) {
      parentHandleChange({
        fieldIdentifier: Field.REPORTED_CONTENT_TYPE,
        value: '',
        valid: true,
      });
    }

    parentHandleChange(element);
    // Only fetch associated content for valid URI
    if (element.valid) {
      // Set loaded state to false here as fetchAssociatedContent is debounced
      setEntityType({ loaded: false, requiresAssetExistenceCheck: false });
      fetchEntityType(element.value);
    }
  };

const validateFieldEntityTypeAndContentType = (
  resolvedContentType: EntityType,
  isPodcastEnabledForReporting: boolean,
  isEmployee: boolean,
): boolean => {
  if (!resolvedContentType) {
    return true;
  }

  return (
    !PODCAST_ENTITY_TYPES.includes(entityTypeFromJSON(resolvedContentType)) ||
    isPodcastEnabledForReporting ||
    isEmployee
  );
};

const fetchAssetExistence = (
  parentHandleChange: OnFieldChangeFunction,
  clientAssetExists: (_: ResolveAssetTypeRequest) => Promise<ResolveAssetTypeResponse>,
  setLoading: (_: boolean) => void,
  setAssetExistenceCheck: (_: AssetExistenceCheck) => void,
  entityType: ResolveEntityType,
): ((assetType: string) => Promise<void>) => {
  return async assetTypeString => {
    const { requiresAssetExistenceCheck, uri } = entityType;
    parentHandleChange({
      fieldIdentifier: Field.REPORTED_CONTENT_TYPE,
      value: assetTypeString,
      valid: true,
    });

    if (!requiresAssetExistenceCheck) {
      return;
    }

    setLoading(true);
    let assetExists: boolean = false;
    let error: string = '';
    try {
      const assetType = associatedContentTypeFromJSON(assetTypeString);

      const request: ResolveAssetTypeRequest = {
        uri: convertPlaylistV1ToV2(getValidSpotifyUri(uri)),
        associatedContentType: assetType,
      };

      const responseJson: ResolveAssetTypeResponse = await clientAssetExists(request);
      assetExists = responseJson.assetExists;
      if (!assetExists) {
        error = ErrorMessage.ASSET_NON_EXISTENT_ERROR;
      }
    } catch (e) {
      error = ErrorMessage.CONTENT_REQUEST_ERROR;
    } finally {
      parentHandleChange({
        fieldIdentifier: Field.REPORTED_CONTENT_TYPE,
        value: assetTypeString,
        valid: !requiresAssetExistenceCheck || assetExists,
      });
      setAssetExistenceCheck({
        error,
        assetExists,
        loaded: true,
      });
      setLoading(false);
    }
  };
};

const fetchEntityType = (
  clientResolveEntityType: (_: ResolveEntityTypeRequest) => Promise<ResolveEntityTypeResponse>,
  setLoading: (_: boolean) => void,
  setResolvedEntityType: (_: ResolveEntityType) => void,
  parentHandleChange: OnFieldChangeFunction,
  market: string,
  isEmployee: boolean,
): ((uri: string) => Promise<void>) => {
  return async uri => {
    setLoading(true);
    const request: ResolveEntityTypeRequest = {
      uris: [convertPlaylistV1ToV2(getValidSpotifyUri(uri))],
    };
    let exists: boolean = false;
    let error: string = '';
    let resolvedEntityType: undefined | EntityType = undefined;
    let isValidFieldBasedOnResolvedEntityTypeAndMarket: boolean = true;
    try {
      const responseJson: ResolveEntityTypeResponse = await clientResolveEntityType(request);
      exists = !!responseJson.resolvedEntityType && responseJson.resolvedEntityType[0].entityExists;
      if (!exists) {
        error = ErrorMessage.CONTENT_NON_EXISTENT_ERROR;
      } else {
        resolvedEntityType = responseJson.resolvedEntityType[0].entityType;
        isValidFieldBasedOnResolvedEntityTypeAndMarket = validateFieldEntityTypeAndContentType(
          resolvedEntityType,
          isPodcastReportingEnabledForMarket(market),
          isEmployee,
        );

        if (!isValidFieldBasedOnResolvedEntityTypeAndMarket) {
          error = ErrorMessage.CONTENT_URI_NOT_ACCEPTED_ERROR;
        }
      }
    } catch (e) {
      error = ErrorMessage.CONTENT_REQUEST_ERROR;
    } finally {
      const uriValid = exists && isValidFieldBasedOnResolvedEntityTypeAndMarket;
      parentHandleChange({
        fieldIdentifier: Field.REPORTED_CONTENT_URIS,
        value: uri,
        valid: uriValid,
      });

      const requiresAssetExistenceCheck =
        resolvedEntityType !== undefined &&
        ASSET_EXISTENCE_CHECK_ENTITIES.includes(entityTypeFromJSON(resolvedEntityType));

      setResolvedEntityType({
        uri,
        error: error,
        entityType: resolvedEntityType,
        loaded: true,
        requiresAssetExistenceCheck,
      });

      setLoading(false);
    }
  };
};

const getContentTypeOptions = (
  acceptedContentTypeMap: Record<EntityType, Array<ContentType>>,
  uriValue: string,
  uriValid: boolean,
  resolveEntityType: ResolveEntityType,
) => {
  if (!resolveEntityType.loaded) {
    return [];
  }

  return getContentTypeOptionsForUri(acceptedContentTypeMap, uriValue, uriValid, resolveEntityType.entityType);
};

export interface Step2ReportedContentUriAndTypeProps extends MarketInfoProps, TintinClientProps, ContextualInfoProps {
  acceptedUriTypes: Array<CONTENT_TYPES>;
  uriToContentTypeMappings: Record<EntityType, Array<ContentType>>;
  forceValidation: boolean;
  onFieldChange: OnFieldChangeFunction;
  fieldValue: (fieldIdentifier: string) => string;
  fieldValid: (fieldIdentifier: string) => boolean;
  setLoading: (_: boolean) => void;
  isEmployee?: boolean;
}

interface ResolveEntityType {
  uri?: string;
  error?: string;
  entityType?: EntityType;
  loaded: boolean;
  requiresAssetExistenceCheck?: boolean;
}

interface AssetExistenceCheck {
  error?: string;
  assetExists?: boolean;
  loaded: boolean;
}

const Step2ReportedContentUriAndType = ({
  acceptedUriTypes,
  uriToContentTypeMappings,
  forceValidation,
  onFieldChange,
  fieldValue,
  fieldValid,
  client,
  setLoading,
  marketInfo: { market },
  isEmployee = false,
}: Step2ReportedContentUriAndTypeProps): JSX.Element => {
  const uriValue: string = fieldValue(Field.REPORTED_CONTENT_URIS);
  const uriValid: boolean = fieldValid(Field.REPORTED_CONTENT_URIS);

  const [entityType, setEntityType] = useState<ResolveEntityType>({
    uri: uriValue,
    loaded: false,
    requiresAssetExistenceCheck: false,
  });
  const [assetExistenceCheck, setAssetExistenceCheck] = useState<AssetExistenceCheck>({
    loaded: false,
  });

  const checkWhetherAssetExists = useMemo(
    () => fetchAssetExistence(onFieldChange, client.checkAssetExists, setLoading, setAssetExistenceCheck, entityType),
    [onFieldChange, client, setLoading, setAssetExistenceCheck, entityType],
  );

  const resolvedEntityType = useMemo(
    () => fetchEntityType(client.resolveEntityType, setLoading, setEntityType, onFieldChange, market, isEmployee),
    [client, setLoading, setEntityType, onFieldChange, market, isEmployee],
  );

  const debouncedResolveEntityType = useMemo(
    () => debounce(resolvedEntityType, RESOLVE_ENTITY_TYPE_DEBOUNCE_TIME_IN_MS),
    [resolvedEntityType],
  );

  return (
    <Fragment>
      <SingleUriField
        acceptedUriTypes={acceptedUriTypes}
        labelOverride={withFullTranslationKey(FieldProperties[Field.REPORTED_CONTENT_URIS].label)}
        errorMessageKey={getUriErrorMessage(uriValue, acceptedUriTypes, entityType.error ?? '')}
        field={{
          name: Field.REPORTED_CONTENT_URIS,
          type: 'text',
          required: true,
          maxLength: 80,
        }}
        value={uriValue}
        valid={uriValid}
        onFieldChange={handleUriInputChange(
          fieldValue(Field.REPORTED_CONTENT_URIS),
          onFieldChange,
          debouncedResolveEntityType,
          setEntityType,
        )}
        // This is to validated initial value of URI from query param.
        forceValidation={!!uriValue || forceValidation}
        validateOnChange
      />

      <SelectField
        field={{
          name: Field.REPORTED_CONTENT_TYPE,
          type: 'text',
          options: getContentTypeOptions(uriToContentTypeMappings, uriValue, uriValid, entityType),
          required: true,
          labelOverride: USE_LABEL_FROM_TRANSLATION_PREFIX,
        }}
        hasDefaultPlaceholder
        value={fieldValue(Field.REPORTED_CONTENT_TYPE)}
        valid={fieldValid(Field.REPORTED_CONTENT_TYPE)}
        errorMessageKey={withFullTranslationKey(assetExistenceCheck.error)}
        tPrefix={withFullTranslationKey(FieldProperties[Field.REPORTED_CONTENT_TYPE].translationPrefix)}
        onFieldChange={handleAssetTypeChange(onFieldChange, checkWhetherAssetExists)}
        forceValidation={forceValidation}
        validateOnChange
      />
    </Fragment>
  );
};

export default withTintinClient(withMarketInfo(withContextualInfo(Step2ReportedContentUriAndType)));
